/**
 * Utility functions for date formatting
 */

/**
 * Safely format date to Indonesian locale
 * Returns formatted date or 'Tanggal tidak valid' if invalid
 */
export const formatDateIndonesian = (dateStr) => {
  if (!dateStr) return 'Tanggal tidak tersedia';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return 'Tanggal tidak valid';
    }
    
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Tanggal tidak valid';
  }
};

/**
 * HOTFIX: Format order date with day name (NO FALLBACK if date exists)
 * Returns: "Sen, 10 Nov 2025" or empty string if no date
 */
export const formatOrderDate = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return date.toLocaleDateString('id-ID', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Safely format date to short format (DD/MM/YYYY)
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return 'Invalid';
    }
    
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid';
  }
};

// =========================================================================
// Feb 2026 — Central UTC → WIB (Asia/Jakarta, UTC+7) formatters
// -------------------------------------------------------------------------
// Use these for ANY audit-trail / server timestamp (created_at, updated_at,
// verified_at, completed_at, approved_at, etc.). They deterministically render
// in WIB regardless of the browser's local timezone.
// =========================================================================

const _WIB_OPTS_DATETIME = {
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Jakarta',
};

const _WIB_OPTS_DATETIME_SECONDS = {
  ..._WIB_OPTS_DATETIME,
  second: '2-digit',
};

const _WIB_OPTS_DATE = {
  day: '2-digit', month: 'short', year: 'numeric',
  timeZone: 'Asia/Jakarta',
};

const _WIB_OPTS_TIME = {
  hour: '2-digit', minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Jakarta',
};

/**
 * Format a UTC ISO string (or Date) as WIB (Asia/Jakarta) datetime with " WIB" suffix.
 * Returns "-" when input is nullish, and echoes back the raw string on parse failure.
 * Example: "2026-02-07T07:30:00Z" → "07 Feb 2026, 14.30 WIB"
 */
export const formatWIB = (input, { withSeconds = false, showSuffix = true } = {}) => {
  if (!input) return '-';
  try {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return typeof input === 'string' ? input : '-';
    const out = d.toLocaleString('id-ID', withSeconds ? _WIB_OPTS_DATETIME_SECONDS : _WIB_OPTS_DATETIME);
    return showSuffix ? `${out} WIB` : out;
  } catch (_e) {
    return typeof input === 'string' ? input : '-';
  }
};

/**
 * Format a UTC timestamp as WIB date only (no time). Example: "07 Feb 2026".
 */
export const formatWIBDate = (input) => {
  if (!input) return '-';
  try {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return typeof input === 'string' ? input : '-';
    return d.toLocaleDateString('id-ID', _WIB_OPTS_DATE);
  } catch (_e) {
    return typeof input === 'string' ? input : '-';
  }
};

/**
 * Format a UTC timestamp as WIB time only. Example: "14.30 WIB".
 */
export const formatWIBTime = (input, { showSuffix = true } = {}) => {
  if (!input) return '-';
  try {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return typeof input === 'string' ? input : '-';
    const out = d.toLocaleTimeString('id-ID', _WIB_OPTS_TIME);
    return showSuffix ? `${out} WIB` : out;
  } catch (_e) {
    return typeof input === 'string' ? input : '-';
  }
};

/**
 * Baca stempel waktu dari server menjadi Date yang benar.
 * Server kadang menyimpan UTC polos tanpa penanda zona (mis. ack_deadline_at
 * "2026-11-03T15:56:00"). Browser membaca string seperti itu sebagai jam
 * lokal, sehingga tampil kurang 7 jam di WIB. Di sini string tanpa penanda
 * zona dianggap UTC. String dengan "Z" atau offset (+07:00) dipakai apa adanya.
 */
export const parseServerUtc = (input) => {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  let s = String(input).trim();
  const hasTime = s.includes('T') || /\d{2}:\d{2}/.test(s);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  if (hasTime && !hasZone) {
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
